import {ChangeDetectionStrategy, Component, DestroyRef, Injector, OnInit, inject, input} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {BzTableColumnSettings} from '@bringoz/ui';
import {Observable} from 'rxjs';
import {distinctUntilChanged, finalize, skip} from 'rxjs/operators';
import * as moment from 'moment';
import * as _ from 'lodash';
import {TimekeepingListStore} from '../../state/timekeeping-list.store';
import {TimekeepingExportService} from '../../services/timekeeping-export.service';
import {TimekeepingDialogsService} from '../../services/timekeeping-dialogs.service';
import {PanelService} from '../../../../commons/angular/services/panel.service';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {SplitService} from '../../../../commons/services/split-service.service';
import type {BringozToastService} from '../../../../commons/services/bringoz-toast.service';
import {SiteFiltersType} from '../../../../commons/types/site-filters.type';
import {ExportType} from '../../../../commons/types/export.type';
import {TimeCardStatusType} from '../../../types/timecard-status.type';
import {TimeCardDisplayStatusGroupType} from '../../../types/timecard-display-status-group.type';
import {TimekeepingExportPayrollPayloadModel} from '../../../models/timekeeping-export-payroll-payload.model';
import {TimekeepingExportMileagePayloadModel} from '../../../models/timekeeping-export-mileage-payload.model';
import {TimekeepingExportTimecardsPayloadModel} from '../../../models/timekeeping-export-timecards-payload.model';
import {TimekeepingExportMondelezSummaryPayloadModel} from '../../../models/timekeeping-export-mondelez-summary-payload.model';
import {resolveFieldValue} from '../../../utils/field.util';
import {
  TimekeepingDateRange,
  TimekeepingSelectTimecardEvent,
  TimekeepingViewSettings
} from '../../models/timekeeping-list.model';
import {
  TimekeepingColumnsSettingsAction,
  TimekeepingColumnsSettingsPanelComponent,
  TimekeepingColumnsSettingsResult
} from '../timekeeping-columns-settings-panel/timekeeping-columns-settings-panel.component';

interface UiRouterStateService {
  current: { name: string };

  go(state: string, params?: Record<string, unknown>, options?: Record<string, unknown>): unknown;
}

/**
 * Smart container of the timecards list: owns the list store, the API calls, dialogs and navigation.
 * Everything below it (totals, toolbar, table) only renders inputs and emits events.
 */
require('./timekeeping-view.scss');
@Component({
  standalone: false,
  selector: 'app-timekeeping-view',
  templateUrl: './timekeeping-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TimekeepingListStore]
})
export class TimekeepingViewComponent implements OnInit {

  // Route params, parsed by the 'timekeeping' state controller
  readonly from = input<number>();
  readonly to = input<number>();
  readonly groupTags = input<string[]>();
  readonly groupIds = input<string[]>();
  readonly branchIds = input<string[]>();
  readonly tags = input<string[]>();
  readonly statuses = input<string[]>();
  readonly displayStatusGroups = input<string[]>();
  readonly queryString = input<string>();

  readonly store = inject(TimekeepingListStore);

  private readonly timekeepingExportService = inject(TimekeepingExportService);
  private readonly panelService = inject(PanelService);
  private readonly translate = inject(TranslateService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly $state = injectLegacy<UiRouterStateService>('$state');
  private readonly splitService = injectLegacy<SplitService>('splitService');
  private readonly bringozToastService = injectLegacy<BringozToastService>('bringozToastService');
  private readonly dialogs = inject(TimekeepingDialogsService);

  ngOnInit() {
    this.store.init({
      from: this.from(),
      to: this.to(),
      groupTags: this.groupTags(),
      groupIds: this.groupIds(),
      branchIds: this.branchIds(),
      tags: this.tags(),
      statuses: this.statuses(),
      displayStatusGroups: this.displayStatusGroups(),
      queryString: this.queryString()
    }, this.$state.current.name, this.splitService.getSiteTimekeepingSettings());

    // The first value is the state the route was opened with, so only later changes are written back.
    toObservable(this.store.urlParams, {injector: this.injector})
      .pipe(distinctUntilChanged(_.isEqual), skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => this.$state.go('timekeeping', params, {notify: false, location: 'replace'}));

    this.store.payrollExport$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((process) => this.dialogs.payrollExportRecovery({batchId: process.batchId, stalled: process.stalled})
        .then((following) => this.store.resolvePayrollExport(following)));
  }

  // ******************************
  //        Filters
  // ******************************

  onTotalsSelect(status: TimeCardDisplayStatusGroupType) {
    this.store.toggleTotalsStatus(status);
  }

  onSearchChange(query: string) {
    this.store.setSearchQuery(query);
  }

  onStatusesChange(statuses: string[]) {
    this.store.setStatuses(statuses);
  }

  onAgentTagsChange(tags: string[]) {
    this.store.setAgentTags(tags);
  }

  onGroupTagsChange(groupTags: string[]) {
    this.store.setGroupTags(groupTags);
  }

  onGroupIdsChange(groupIds: string[]) {
    this.store.setGroupIds(groupIds);
  }

  onBranchIdsChange(branchIds: string[]) {
    this.store.setBranchIds(branchIds);
  }

  onRangeChange(range: TimekeepingDateRange) {
    this.store.setRange(range);
  }

  onBrowseWeek(isNext: boolean) {
    this.store.browseWeek(isNext);
  }

  // ******************************
  //        Table
  // ******************************

  onSelectionChange(selectedIds: string[]) {
    this.store.setSelection(selectedIds);
  }

  onLoadMore() {
    this.store.fetchNext();
  }

  onTableSettingsChange(table: BzTableColumnSettings) {
    this.store.setTableSettings(table);
    this.splitService.setSiteTimekeepingSettings(this.store.settings());
  }

  onSelectTimecard(event: TimekeepingSelectTimecardEvent) {
    const {timecard, group, isAgentView} = event;
    const agentId = timecard?.agentId || group?.agentId;
    const dateStamp = (timecard ? resolveFieldValue(timecard.clockInTimeField) : null) || group?.startTime || moment().valueOf();
    const fromDate = moment(dateStamp).startOf('day').valueOf();
    const toDate = moment(dateStamp).endOf('day').valueOf();

    if (isAgentView) {
      this.$state.go('agent.timecards', {id: agentId, from: fromDate, to: toDate});
      return;
    }

    const range = this.store.range();
    const filters = this.store.filters();
    this.$state.go('timecardDetail', {
      agentId: agentId,
      from: fromDate,
      to: toDate,
      listFrom: range.dateStart,
      listTo: range.dateEnd,
      groupTags: filters.groupTags,
      groupIds: filters[SiteFiltersType.groupIds],
      branchIds: filters[SiteFiltersType.branchIds],
      tags: filters[SiteFiltersType.tags],
      statuses: filters.statuses,
      displayStatusGroups: filters[SiteFiltersType.displayStatusGroups],
      query: this.store.searchQuery() || null
    });
  }

  // ******************************
  //        Complete
  // ******************************

  onComplete() {
    const {approvedIds, skippedCount} = this.store.getApprovableSelection();

    if (!approvedIds.length) {
      this.bringozToastService.error({body: 'TIMECARD_COMPLETE_NO_APPROVED'});
      return;
    }

    const title = this.translate.instant('Complete Timecards');
    let message = this.translate.instant('TIMECARD_COMPLETE_CONFIRMATION', {count: approvedIds.length});

    if (skippedCount > 0) {
      const skippedMessage = this.translate.instant('TIMECARD_COMPLETE_SKIPPED', {count: skippedCount});
      message = `${message}\n\n${skippedMessage}`;
    }

    this.dialogs.confirm(title, message)
      .then((confirmed) => confirmed && this.store.complete(approvedIds));
  }

  // ******************************
  //        Exports
  // ******************************

  onExportTimecards() {
    if (this.store.exporting()) {
      return;
    }

    const payload: TimekeepingExportTimecardsPayloadModel = {
      branchIds: this.store.queryBranchIds(),
      timeCardStatuses: [TimeCardStatusType.Open, TimeCardStatusType.Closed, TimeCardStatusType.Acknowledged],
      from: this.store.range().dateStart,
      to: this.store.range().dateEnd,
      exportType: ExportType.Excel
    };

    this.runExport(this.timekeepingExportService.exportTimeCards(payload));
  }

  onExportMileage() {
    if (this.store.exporting()) {
      return;
    }

    const payload: TimekeepingExportMileagePayloadModel = {
      branchIds: this.store.queryBranchIds(),
      statuses: [TimeCardStatusType.Approved],
      from: this.store.range().dateStart,
      to: this.store.range().dateEnd,
      exportType: ExportType.Excel,
      fieldTranslationMap: {
        agentExternalId: this.translate.instant('FILE_EXPORT.TIMEKEEPING.MILEAGE_COLUMNS.External ID'),
        agentName: this.translate.instant('FILE_EXPORT.TIMEKEEPING.MILEAGE_COLUMNS.Agent Name'),
        totalDistance: this.translate.instant('FILE_EXPORT.TIMEKEEPING.MILEAGE_COLUMNS.Total Distance'),
        totalDayDistance: this.translate.instant('FILE_EXPORT.TIMEKEEPING.MILEAGE_COLUMNS.Total Day Distance'),
        date: this.translate.instant('FILE_EXPORT.TIMEKEEPING.MILEAGE_COLUMNS.Date'),
        weekEndingDate: this.translate.instant('FILE_EXPORT.TIMEKEEPING.MILEAGE_COLUMNS.Week Ending Date')
      },
      zoneId: this.splitService.getTimeZone()
    };

    this.runExport(this.timekeepingExportService.exportMileage(payload));
  }

  onExportPayroll() {
    if (this.store.exportingPayroll()) {
      return;
    }

    this.dialogs.exportPayroll()
      .then((options) => {
        if (!options) {
          return;
        }
        const payload: TimekeepingExportPayrollPayloadModel = {
          fieldTranslationMap: {
            agentExternalId: this.translate.instant('FILE_EXPORT.TIMEKEEPING.PAYROLL_COLUMNS.External ID'),
            payCode: this.translate.instant('FILE_EXPORT.TIMEKEEPING.PAYROLL_COLUMNS.PayCode'),
            date: this.translate.instant('FILE_EXPORT.TIMEKEEPING.PAYROLL_COLUMNS.Date'),
            totalPaidHours: this.translate.instant('FILE_EXPORT.TIMEKEEPING.PAYROLL_COLUMNS.Total Paid Hours'),
            timeCardType: this.translate.instant('FILE_EXPORT.TIMEKEEPING.PAYROLL_COLUMNS.Timecard Type')
          },
          branchIds: this.store.queryBranchIds(),
          timeCardStatuses: [TimeCardStatusType.Approved, TimeCardStatusType.Completed],
          from: this.store.range().dateStart,
          to: this.store.range().dateEnd,
          exportType: ExportType.CSV,
          zoneId: this.splitService.getTimeZone(),
          completeApproved: options.completeApproved,
          archiveNotApproved: options.archiveNotApproved
        };

        this.store.exportPayroll(payload);
      });
  }

  onExportMondelezSummary() {
    if (this.store.exporting()) {
      return;
    }

    this.store.setExporting(true);

    this.dialogs.mondelezSummary()
      .then((range) => {
        if (!range) {
          this.store.setExporting(false);
          return;
        }
        const payload: TimekeepingExportMondelezSummaryPayloadModel = range;

        this.runExport(this.timekeepingExportService.exportMondelezSummary(payload));
      });
  }

  // ******************************
  //        Settings
  // ******************************

  onOpenSettings(event: MouseEvent) {
    this.panelService.open(TimekeepingColumnsSettingsPanelComponent, event, {settings: this.store.settings()}, {
      position: {originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4}
    }).afterClose$.subscribe((result: TimekeepingColumnsSettingsResult | null) => {
      // A null result is a click outside the panel - the settings stay as they are.
      if (!result) {
        return;
      }
      // Reset and Cancel both drop the unsaved changes and fall back to the stored settings.
      const settings: TimekeepingViewSettings = result.action === TimekeepingColumnsSettingsAction.Submit
        ? result.settings
        : this.splitService.getSiteTimekeepingSettings(this.splitService.getSiteId());
      this.store.setSettings(settings);
      this.splitService.setSiteTimekeepingSettings(settings);
    });
  }

  private runExport(export$: Observable<void>) {
    this.store.setExporting(true);
    export$
      .pipe(finalize(() => this.store.setExporting(false)), takeUntilDestroyed(this.destroyRef))
      // Errors are already surfaced as a toast by TimekeepingExportService.
      .subscribe({error: () => null});
  }
}
